import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, TrendingUp, Shield, Eye, Wallet,
  Users, Award, FileText,
} from "lucide-react";
import Layout from "../components/Layout";

export default function ProgramaAdministradores() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative grain" style={{ background: "linear-gradient(180deg, #FAFAF8 0%, #FFFFFF 100%)" }} data-testid="programa-hero">
        <div className="container-x py-20 md:py-28">
          <div className="hero-grid">
            <div className="fade-in-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-6" style={{ borderColor: "var(--border-subtle)", background: "white" }}>
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--brand-accent)" }}></div>
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--text-muted)" }}>
                  Programa para administradores de edificios
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl leading-[0.95] font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
                Tú conectas.<br />
                <span style={{ color: "var(--brand-accent)" }}>Nosotros gestionamos.</span><br />
                <span className="italic font-light" style={{ color: "var(--text-muted)" }}>Tú ganas.</span>
              </h1>

              <p className="mt-7 text-lg md:text-xl leading-relaxed max-w-xl" style={{ color: "var(--text-muted)" }}>
                Un programa transparente para administradores que quieren agregar valor real a sus comunidades. Refiere edificios, deja que nosotros hagamos el trabajo y recibe <strong style={{ color: "var(--text-primary)" }}>comisiones declaradas</strong> por cada póliza contratada.
              </p>

              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/partner/register" className="btn-primary" data-testid="cta-registrarse">
                  Unirme al programa
                  <ArrowRight size={17} />
                </Link>
                <Link to="/partner/login" className="btn-outline" data-testid="cta-partner-login">
                  Ya soy partner
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 pt-8 border-t" style={{ borderColor: "var(--border-subtle)" }}>
                <div>
                  <div className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope", color: "var(--brand-accent)" }}>12%</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>comisión sobre prima anual</div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>0</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>papeleo para ti</div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>24h</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>aprobación de tu cuenta</div>
                </div>
              </div>
            </div>

            {/* Mock dashboard preview */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 rounded-full opacity-20" style={{ background: "var(--brand-accent)" }}></div>
              <div className="relative rounded-2xl border bg-white p-6 shadow-xl" style={{ borderColor: "var(--border-subtle)" }} data-testid="dashboard-preview">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--brand-accent)" }}>Tu panel</div>
                    <div className="font-bold mt-0.5" style={{ fontFamily: "Manrope" }}>Resumen del mes</div>
                  </div>
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--brand-navy)" }}>
                    <Wallet size={16} color="white" />
                  </div>
                </div>

                <div className="space-y-3 mb-5">
                  {[
                    { label: "Comunidades referidas", value: "12", icon: Users },
                    { label: "Pólizas contratadas", value: "8", icon: Shield },
                    { label: "Comisión acumulada", value: "UF 84,5", highlight: true, icon: TrendingUp },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: "var(--border-subtle)", background: row.highlight ? "rgba(217,119,87,0.06)" : "transparent" }}>
                      <div className="flex items-center gap-2.5">
                        <row.icon size={16} style={{ color: row.highlight ? "var(--brand-accent)" : "var(--text-muted)" }} />
                        <span className="text-sm" style={{ color: "var(--text-muted)" }}>{row.label}</span>
                      </div>
                      <div className="font-bold" style={{ fontFamily: "Manrope", color: row.highlight ? "var(--brand-accent)" : "var(--text-primary)" }}>
                        {row.value}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: "var(--text-muted)" }}>Últimas referencias</div>
                <div className="space-y-2">
                  {[
                    { t: "Edificio Las Higueras", s: "contratada", uf: "UF 14,2" },
                    { t: "Condominio Los Álamos", s: "cotizada", uf: "UF 0" },
                    { t: "Edificio Vista Mar", s: "contactada", uf: "UF 0" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-[13px] p-2.5 rounded-md" style={{ background: "var(--bg-soft)" }}>
                      <span className="font-medium">{r.t}</span>
                      <div className="flex items-center gap-3">
                        <span className={`badge badge--${r.s === "contratada" ? "vigente" : r.s === "cotizada" ? "en_revision" : "nueva"}`}>{r.s}</span>
                        {r.uf !== "UF 0" && <span className="font-bold text-[12px]" style={{ color: "var(--brand-accent)" }}>{r.uf}</span>}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t text-center text-[11px]" style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}>
                  Datos de ejemplo · disponible al registrarte
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="py-24" style={{ background: "var(--bg-cream)" }}>
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: "var(--brand-accent)" }}>Cómo funciona</div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
              Tres pasos. Cero fricción.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Te registras", d: "Completas un formulario de 2 minutos. Aprobamos tu cuenta en menos de 24 hrs hábiles." },
              { n: "02", t: "Refieres comunidades", d: "Desde tu panel ingresas el nombre del edificio y el contacto. Nosotros gestionamos todo el proceso." },
              { n: "03", t: "Cobras comisiones", d: "Cuando la comunidad contrata, tu comisión queda registrada automáticamente y se paga según acuerdo." },
            ].map((s, i) => (
              <div key={i} className="bg-white p-7 border rounded-xl" style={{ borderColor: "var(--border-subtle)" }} data-testid={`step-${i}`}>
                <div className="text-4xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "Manrope", color: "var(--brand-accent)" }}>{s.n}</div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "Manrope" }}>{s.t}</h3>
                <p className="text-[15px]" style={{ color: "var(--text-muted)" }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="py-24">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: "var(--brand-accent)" }}>Beneficios</div>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-5" style={{ fontFamily: "Manrope" }}>
                Hecho para administradores que valoran la transparencia.
              </h2>
              <p className="text-lg" style={{ color: "var(--text-muted)" }}>
                Sin presión sobre residentes ni comité. Sin conflicto de interés. Comisiones declaradas a la comunidad — exactamente como debe ser.
              </p>
            </div>

            <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
              {[
                { icon: Eye, t: "Comisiones transparentes", d: "Tu comisión queda declarada a la comunidad antes de contratar. Cumple con la normativa CMF." },
                { icon: Wallet, t: "12% sobre prima anual", d: "Comisión competitiva del mercado, pagada al contratar y en cada renovación." },
                { icon: Shield, t: "Cero responsabilidad legal", d: "Tú refieres, nosotros somos el corredor responsable ante la CMF y la comunidad." },
                { icon: TrendingUp, t: "Panel en tiempo real", d: "Ves el estado de cada referencia, ingresos acumulados y pólizas activas en vivo." },
                { icon: FileText, t: "Documentación lista", d: "Generamos cartas de asignación, cotizaciones comparadas y contratos por ti." },
                { icon: Award, t: "Soporte dedicado", d: "Un ejecutivo asignado para tu cartera de comunidades. Respuesta en 24h hábiles." },
              ].map((b, i) => (
                <div key={i} className="card-marketing" style={{ padding: "1.5rem" }}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4" style={{ background: "rgba(217,119,87,0.15)" }}>
                    <b.icon size={18} style={{ color: "var(--brand-accent)" }} />
                  </div>
                  <h3 className="font-bold text-lg mb-1.5" style={{ fontFamily: "Manrope" }}>{b.t}</h3>
                  <p className="text-[14px]" style={{ color: "var(--text-muted)" }}>{b.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CALCULADORA EJEMPLO */}
      <section className="py-24" style={{ background: "var(--bg-soft)" }}>
        <div className="container-x">
          <div className="rounded-2xl p-10 md:p-16 text-white" style={{ background: "var(--brand-navy)" }}>
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: "var(--brand-accent)" }}>Ejemplo real</div>
                <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
                  Si administras 8 edificios...
                </h2>
                <p className="mt-4 opacity-80 max-w-md">
                  Y refieres todas tus comunidades, esto puedes generar al año (cifras estimadas, basadas en primas promedio del mercado).
                </p>
              </div>

              <div className="bg-white text-black rounded-xl p-7" style={{ fontFamily: "Manrope" }}>
                <div className="space-y-4">
                  {[
                    { label: "8 comunidades · prima media UF 35", val: "UF 280" },
                    { label: "Comisión 12% sobre primas", val: "UF 33,6" },
                    { label: "Equivalente CLP (UF $39.000)", val: "$1.310.400" },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between items-baseline pb-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
                      <span className="text-sm" style={{ color: "var(--text-muted)" }}>{r.label}</span>
                      <span className={`font-extrabold ${i === 2 ? "text-2xl" : "text-lg"}`} style={{ color: i === 2 ? "var(--brand-accent)" : "var(--text-primary)" }}>{r.val}</span>
                    </div>
                  ))}
                  <div className="pt-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                    Ingreso recurrente — cada año al renovar pólizas.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24 pt-12">
        <div className="container-x text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl mx-auto" style={{ fontFamily: "Manrope" }}>
            ¿Listo para sumar valor a tu cartera?
          </h2>
          <p className="mt-4 text-lg max-w-xl mx-auto" style={{ color: "var(--text-muted)" }}>
            Aprobamos cuentas en menos de 24 horas hábiles. Sin costo de inscripción.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/partner/register" className="btn-primary" data-testid="final-register">
              Crear cuenta partner
              <ArrowRight size={16} />
            </Link>
            <Link to="/partner/login" className="btn-outline">
              Ya tengo cuenta
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
